import { getDomainId } from 'api-client/helpers'
import { listTablePrefs } from 'app/constants'
import { RootState } from 'app/store'
import { ArrayElement } from 'core/actions/Action'
import DocumentMeta from 'core/components/DocumentMeta'
import ListContainer from 'core/containers/ListContainer'
import { GridViewColumn } from 'core/elements/grid/Grid'
import getGridDialogButton from 'core/elements/grid/helpers/getGridDialogButton'
import { GridBatchActionSpec } from 'core/elements/grid/hooks/useGridSelectableRows'
import useListAction from 'core/hooks/useListAction'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import useSelectorWithParams from 'core/hooks/useSelectorWithParams'
import { SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import { routeManager } from 'core/utils/routes'
import DataKeys from 'k8s/DataKeys'
import { pick, prop } from 'ramda'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import DomainFilter from '../domains/DomainFilter'
import CreateUserModal from './CreateUserModal'
import UserDeleteDialog from './delete-user-dialog'
import EditUserModal from './EditUserModal'
import EnableDisableUserDialog from './enable-disable-user-dialog'
import { listUsers } from './new-actions'
import { usersSelector } from './selectors'
import UserQuotasModal from './UserQuotasModal'

type ModelDataKey = DataKeys.ManagementUsers
type SelectorModel = ArrayElement<ReturnType<typeof usersSelector>>

const defaultParams = {}
const usePrefParams = createUsePrefParamsHook('ManagementUsers', listTablePrefs)

const isEnabledUser = (user) => user?.enabled

const searchTargets = ['username', 'id', 'displayname']

const columns: GridViewColumn<SelectorModel>[] = [
  { key: 'username', label: 'Username' },
  { key: 'id', label: 'User Uuid', display: false },
  { key: 'domain', label: 'Domain' },
  { key: 'displayname', label: 'Display Name' },
  { key: 'enabled', label: 'Status', formatFn: (val: boolean) => (val ? 'Enabled' : 'Disabled') },
]

export default function UsersListPage() {
  const [showEditUserModal, setShowEditUserModal] = useState<boolean>(false)
  const [showUserQuotasModal, setShowUserQuotasModal] = useState<boolean>(false)
  const [showDeleteUserModal, setShowDeleteUserModal] = useState<boolean>(false)
  const [showEnableDisableUserModal, setShowEnableDisableUserModal] = useState<boolean>(false)
  const [selectedDomain, setSelectedDomain] = useState<string>(getDomainId())
  const [selectedUser, setSelectedUser] = useState<SelectorModel>(null)

  const { params, getParamsUpdater } = usePrefParams(defaultParams)
  const { message, loading, reload } = useListAction(listUsers, {
    params: {
      domainId: selectedDomain,
    },
  })
  const data = useSelectorWithParams(usersSelector, params)

  const session = useSelector<RootState, SessionState>(prop(sessionStoreKey))

  const isActiveUser = useCallback(
    (user) => {
      return session?.username === user?.username && getDomainId() === user?.domain_id
    },
    [session, getDomainId],
  )

  useEffect(() => {
    reload(true)
  }, [selectedDomain])

  const batchActions: GridBatchActionSpec<SelectorModel>[] = useMemo(
    () => [
      {
        // Todo: Revesit this condition after SSO is enabled
        // cond: ([user]) => user?.isLocal,
        label: 'Edit',
        icon: 'edit',
        handleAction: ([user]) => {
          setSelectedUser(user)
          setShowEditUserModal(true)
        },
        refreshAfterSuccess: true,
      },
      {
        label: 'Manage Quotas',
        icon: 'chart-pie-simple',
        handleAction: ([user]) => {
          setSelectedUser(user)
          setShowUserQuotasModal(true)
        },
        refreshAfterSuccess: true,
      },
      {
        cond: ([user]) => !isActiveUser(user) && !isEnabledUser(user),
        disabledTooltip: (selected) =>
          isActiveUser(selected[0])
            ? 'You cannot enable the currently active user'
            : isEnabledUser(selected[0])
            ? 'User is already enabled'
            : '',
        label: 'Enable',
        icon: 'user-check',
        handleAction: ([user]) => {
          setSelectedUser(user)
          setShowEnableDisableUserModal(true)
        },
      },
      {
        cond: ([user]) => !isActiveUser(user) && isEnabledUser(user),
        disabledTooltip: (selected) =>
          isActiveUser(selected[0])
            ? 'You cannot disable the currently active user'
            : !isEnabledUser(selected[0])
            ? 'User is already disabled'
            : '',
        label: 'Disable',
        icon: 'user-times',
        handleAction: ([user]) => {
          setSelectedUser(user)
          setShowEnableDisableUserModal(true)
        },
      },
      {
        cond: ([user]) => !isActiveUser(user),
        disabledTooltip: (selected) =>
          isActiveUser(selected[0]) ? 'You cannot delete the currently active user' : '',
        label: 'Delete',
        icon: 'trash-alt',
        handleAction: ([user]) => {
          setSelectedUser(user)
          setShowDeleteUserModal(true)
        },
      },
    ],
    [isActiveUser, isEnabledUser],
  )

  return (
    <>
      <DocumentMeta title="Users" />
      <DomainFilter onChange={setSelectedDomain} value={selectedDomain} />
      {showDeleteUserModal && (
        <UserDeleteDialog onClose={() => setShowDeleteUserModal(false)} rows={[selectedUser]} />
      )}
      {showEnableDisableUserModal && (
        <EnableDisableUserDialog
          onClose={() => setShowEnableDisableUserModal(false)}
          rows={[selectedUser]}
        />
      )}
      <CreateUserModal
        addRoute={routeManager.routes.userManagement.addUser}
        domainId={selectedDomain}
      />
      {showEditUserModal && (
        <EditUserModal onClose={() => setShowEditUserModal(false)} rows={[selectedUser]} />
      )}
      {showUserQuotasModal && (
        <UserQuotasModal onClose={() => setShowUserQuotasModal(false)} rows={[selectedUser]} />
      )}
      <ListContainer<ModelDataKey, SelectorModel>
        dataKey={DataKeys.ManagementUsers}
        searchTargets={searchTargets}
        nameProp="username"
        uniqueIdentifier="id"
        loading={loading}
        loadingMessage={message}
        onRefresh={reload}
        data={data}
        columns={columns}
        addUrl={routeManager.routes.userManagement.addUser.path()}
        addText="Create a New User"
        getParamsUpdater={getParamsUpdater}
        batchActions={batchActions}
        {...pick(listTablePrefs, params)}
      />
    </>
  )
}
